import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Bloqueo por inactividad — porteado de `useInactivityTimeout.ts` y
 * `sessionSettings.ts` de `imprenta` (ADR 0034).
 *
 * **Es un bloqueo de pantalla, no un vencimiento de sesión.** La sesión del
 * servidor sigue viva todo el tiempo: lo único que pasa al cumplirse el plazo es
 * que la interfaz se tapa y pide la contraseña. Quien tenga la cookie —otra
 * pestaña, `curl`, la consola del navegador— sigue entrando igual. Sirve contra
 * quien pasa por al lado de un monitor abierto, que es el riesgo real de una
 * imprenta con puestos compartidos, y **no** contra alguien que ya se llevó la
 * cookie.
 *
 * Decirlo importa porque la pantalla de `imprenta` lo llama «Por seguridad» y es
 * fácil leer ahí una garantía que no da.
 *
 * **No es polling** (invariante 11): son `setTimeout` que no tocan la red. El
 * único request que se dispara es el de volver a entrar, y solo si el plazo se
 * cumplió.
 */

/** Los límites de `imprenta`, tal cual: 5 a 60 minutos, 60 por defecto. */
export const MINIMO_MINUTOS = 5;
export const MAXIMO_MINUTOS = 60;
export const POR_DEFECTO_MINUTOS = 60;
/** Aviso 3 minutos antes, como allá. */
export const AVISO_ANTES_MINUTOS = 3;

export const OPCIONES_MINUTOS = [5, 10, 15, 30, 45, 60] as const;

const CLAVE = "printflow.inactividad.minutos";

/**
 * La preferencia vive en `localStorage`, no en el servidor.
 *
 * Es lo que hace `imprenta` y acá además encaja: el plazo protege **esta
 * pantalla en esta máquina**. Quien usa una notebook propia y un puesto
 * compartido del taller quiere plazos distintos en cada uno, y una preferencia
 * por usuario en Neon le daría el mismo en los dos — además de una escritura a
 * la base por un dato que no sale del navegador.
 */
/**
 * Interpreta el valor crudo guardado.
 *
 * Separado de `localStorage` para poder testearlo: es donde vive la unica
 * logica de esto, y los tests del dashboard corren en `node` — sumar `jsdom`
 * para ejercitar dos `getItem` seria pagar una dependencia por nada.
 */
export function minutosDesde(crudo: string | null): number {
  const valor = Number.parseInt(crudo ?? "", 10);
  if (!Number.isFinite(valor)) return POR_DEFECTO_MINUTOS;
  return acotar(valor);
}

export function leerMinutos(): number {
  if (typeof localStorage === "undefined") return POR_DEFECTO_MINUTOS;
  return minutosDesde(localStorage.getItem(CLAVE));
}

/** Evento propio: `storage` solo avisa a las OTRAS pestañas, no a esta. */
const EVENTO_CAMBIO = "printflow:inactividad";

export function guardarMinutos(minutos: number): number {
  const acotado = acotar(minutos);
  if (typeof localStorage !== "undefined") localStorage.setItem(CLAVE, String(acotado));
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(EVENTO_CAMBIO));
  return acotado;
}

/**
 * La preferencia, viva.
 *
 * Sin esto, cambiar el plazo en el perfil no haría nada hasta recargar la
 * página: el bloqueo seguiría corriendo con el valor viejo y **nada fallaría**,
 * que es la peor forma de que no ande. Escucha el evento propio (esta pestaña) y
 * el `storage` del navegador (las demás).
 */
export function usePreferenciaMinutos(): [number, (m: number) => void] {
  const [minutos, setMinutos] = useState(leerMinutos);

  useEffect(() => {
    const releer = () => setMinutos(leerMinutos());
    window.addEventListener(EVENTO_CAMBIO, releer);
    window.addEventListener("storage", releer);
    return () => {
      window.removeEventListener(EVENTO_CAMBIO, releer);
      window.removeEventListener("storage", releer);
    };
  }, []);

  return [minutos, (m: number) => setMinutos(guardarMinutos(m))];
}

/**
 * Acota al rango permitido.
 *
 * Se acota al **leer** y no solo al escribir: el valor viene de `localStorage`,
 * que lo edita cualquiera desde la consola. Un `0` guardado a mano dejaría la
 * pantalla bloqueándose sola en bucle, y un número enorme apagaría el bloqueo
 * sin que la pantalla de perfil lo refleje.
 */
function acotar(minutos: number): number {
  if (!Number.isFinite(minutos)) return POR_DEFECTO_MINUTOS;
  return Math.min(MAXIMO_MINUTOS, Math.max(MINIMO_MINUTOS, Math.round(minutos)));
}

/** Cuánto falta para el aviso. Nunca menos de un minuto ni más que el plazo. */
export function msDelAviso(minutos: number): number {
  return Math.max(1, minutos - AVISO_ANTES_MINUTOS) * 60_000;
}

export function msDelPlazo(minutos: number): number {
  return minutos * 60_000;
}

const EVENTOS_DE_ACTIVIDAD = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "wheel",
] as const;

/** Los reinicios se limitan a uno por segundo: `mousemove` dispara sin parar. */
const ANTIREBOTE_MS = 1000;

export interface UsoDeInactividad {
  /** `true` cuando se cumplió el plazo y hay que volver a autenticarse. */
  bloqueado: boolean;
  /** `true` en la franja de aviso previo. */
  avisando: boolean;
  /** Se llama al volver a entrar: limpia el bloqueo y reinicia los plazos. */
  desbloquear: () => void;
}

export function useInactividad(minutos: number, habilitado: boolean): UsoDeInactividad {
  const [bloqueado, setBloqueado] = useState(false);
  const [avisando, setAvisando] = useState(false);

  const temporizadores = useRef<{ plazo?: number; aviso?: number }>({});
  const ultimaActividad = useRef<number>(Date.now());
  const antirebote = useRef(false);

  const limpiar = useCallback(() => {
    if (temporizadores.current.plazo) window.clearTimeout(temporizadores.current.plazo);
    if (temporizadores.current.aviso) window.clearTimeout(temporizadores.current.aviso);
    temporizadores.current = {};
  }, []);

  const arrancar = useCallback(() => {
    limpiar();
    setAvisando(false);
    ultimaActividad.current = Date.now();
    temporizadores.current.aviso = window.setTimeout(() => setAvisando(true), msDelAviso(minutos));
    temporizadores.current.plazo = window.setTimeout(() => setBloqueado(true), msDelPlazo(minutos));
  }, [limpiar, minutos]);

  const desbloquear = useCallback(() => {
    setBloqueado(false);
    arrancar();
  }, [arrancar]);

  useEffect(() => {
    if (!habilitado || bloqueado) {
      limpiar();
      return;
    }

    arrancar();

    const alHaberActividad = () => {
      if (antirebote.current) return;
      antirebote.current = true;
      window.setTimeout(() => {
        antirebote.current = false;
      }, ANTIREBOTE_MS);
      arrancar();
    };

    for (const evento of EVENTOS_DE_ACTIVIDAD) {
      document.addEventListener(evento, alHaberActividad, { passive: true });
    }

    /**
     * Volver a una pestaña de fondo.
     *
     * Sin esto el bloqueo no llegaría cuando más falta: los navegadores
     * estrangulan los `setTimeout` de las pestañas ocultas, así que una pestaña
     * que estuvo una hora en segundo plano puede volver **sin haberse
     * bloqueado**. Por eso al reaparecer se compara contra el reloj en vez de
     * confiar en el temporizador.
     */
    const alCambiarVisibilidad = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - ultimaActividad.current >= msDelPlazo(minutos)) setBloqueado(true);
      else arrancar();
    };
    document.addEventListener("visibilitychange", alCambiarVisibilidad);

    return () => {
      limpiar();
      for (const evento of EVENTOS_DE_ACTIVIDAD) {
        document.removeEventListener(evento, alHaberActividad);
      }
      document.removeEventListener("visibilitychange", alCambiarVisibilidad);
    };
  }, [habilitado, bloqueado, arrancar, limpiar, minutos]);

  return { bloqueado, avisando, desbloquear };
}
